import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { YoutubeEmbed } from "@/components/ClipCard";
import { Button } from "@/components/ui/button";
import { Swords, RefreshCw, Trophy, Skull } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function BattlePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pair, setPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(null); // winner id
  const [delta, setDelta] = useState(null);

  const loadPair = useCallback(async () => {
    setLoading(true);
    setVoted(null);
    setDelta(null);
    try {
      const { data } = await api.get("/battle/pair");
      setPair(data);
    } catch (e) {
      toast.error("Not enough clips to battle yet");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPair();
  }, [loadPair]);

  const vote = async (winnerId, loserId) => {
    if (!user) {
      toast.error("Sign in to cast a vote");
      navigate("/login");
      return;
    }
    if (voted) return;
    setVoted(winnerId);
    try {
      const { data } = await api.post("/battle/vote", { winner_id: winnerId, loser_id: loserId });
      setDelta(data.delta);
      toast.success(`ELO +${data.delta}`);
    } catch {
      toast.error("Failed to vote");
      setVoted(null);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-12 pb-24 md:pb-12">
      <div className="mb-8 flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#FF2E55]/40 bg-[#FF2E55]/10 px-3 py-1 text-[10px] font-mono-ui uppercase tracking-widest text-[#FF2E55]">
          <Swords className="h-3 w-3" />
          Head-to-Head Arena
        </div>
        <h1 className="mt-4 font-display text-5xl sm:text-6xl text-white">WHO GOT THE <span className="text-amber-400">BIGGER LAUGH?</span></h1>
        <p className="mt-3 max-w-xl text-slate-300">Pick a side. Every vote shifts the ELO. Skip if it's a tie.</p>
      </div>

      {loading || !pair ? (
        <div className="grid gap-6 md:grid-cols-2">
          <div className="h-96 animate-pulse rounded-xl bg-white/5" />
          <div className="h-96 animate-pulse rounded-xl bg-white/5" />
        </div>
      ) : (
        <div className="relative grid gap-6 md:grid-cols-2">
          <BattleSide
            clip={pair.clip_a}
            opponent={pair.clip_b}
            onVote={vote}
            voted={voted}
            color="amber"
            testid="battle-side-a"
          />
          <BattleSide
            clip={pair.clip_b}
            opponent={pair.clip_a}
            onVote={vote}
            voted={voted}
            color="crimson"
            testid="battle-side-b"
          />

          {/* VS badge */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, rotate: -45 }}
              animate={{ scale: 1, rotate: -8 }}
              transition={{ type: "spring", stiffness: 220 }}
              className="hidden md:flex h-24 w-24 items-center justify-center rounded-full border-4 border-[#8B5CF6] bg-black font-display text-4xl text-white glow-crimson pulse-ring"
            >
              VS
            </motion.div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {voted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-8 text-center"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-2 text-sm font-mono-ui uppercase tracking-widest text-amber-400">
              <Trophy className="h-4 w-4" />
              Vote recorded {delta !== null && <span>· +{delta} ELO</span>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-8 flex justify-center">
        <Button
          onClick={loadPair}
          data-testid="new-battle-btn"
          className="bg-white/5 text-white hover:bg-white/10 border border-white/10 btn-brutal"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {voted ? "Next battle" : "Skip (new pair)"}
        </Button>
      </div>
    </div>
  );
}

function BattleSide({ clip, opponent, onVote, voted, color, testid }) {
  const isWinner = voted === clip.id;
  const isLoser = voted && voted !== clip.id;
  const ring = color === "amber" ? "amber-400" : "[#FF2E55]";

  return (
    <motion.div
      initial={{ opacity: 0, x: color === "amber" ? -40 : 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className={`relative rounded-2xl border p-4 transition-all ${
        isWinner
          ? color === "amber" ? "border-amber-400 glow-amber" : "border-[#FF2E55] glow-crimson"
          : isLoser
            ? "border-white/5 opacity-40"
            : "border-white/10"
      } bg-[#12141C]`}
      data-testid={testid}
    >
      <div className="mb-3 flex items-center justify-between">
        <span className={`font-mono-ui text-[10px] uppercase tracking-widest ${
          color === "amber" ? "text-amber-400" : "text-[#FF2E55]"
        }`}>
          Contender {color === "amber" ? "A" : "B"}
        </span>
        <span className="font-mono-ui text-xs text-slate-400">ELO {clip.elo}</span>
      </div>

      <YoutubeEmbed youtubeId={clip.youtube_id} title={clip.title} />

      <div className="mt-4">
        <h3 className="font-display text-2xl text-white leading-tight">{clip.title}</h3>
        <p className="mt-1 text-sm text-slate-400">
          <span className="text-[#00F0FF]">◆</span> {clip.comedian} · {clip.category === "standup" ? "Stand-up" : "Skit"} · {clip.era}
        </p>
      </div>

      <Button
        onClick={() => onVote(clip.id, opponent.id)}
        disabled={!!voted}
        data-testid={`${testid}-vote-btn`}
        className={`mt-4 w-full btn-brutal font-bold uppercase tracking-widest ${
          color === "amber"
            ? "bg-amber-400 text-black hover:bg-amber-300"
            : "bg-[#FF2E55] text-black hover:bg-[#e02245]"
        }`}
      >
        {isWinner ? <><Trophy className="mr-2 h-4 w-4" />Winner</> : isLoser ? <><Skull className="mr-2 h-4 w-4" />Bombed</> : "This One's Funnier"}
      </Button>
    </motion.div>
  );
}
